import { Component } from "react";
import { isStaging, isLocal } from "./../environment.js";
import Rollbar from "rollbar";
import { auth } from "src/firebase/firebase.js";
import { customEvent } from "src/utils/gtag.js";
export * from "./ErrorComponent.js";

/**
 * Rollbar transform: a string `rollbarFingerprint` in custom data overrides
 * Rollbar's default grouping.
 *
 * @param {Record<string, any>} data Item payload `data`, mutated in place.
 */
export function promoteCustomFingerprint(data) {
  if (typeof data?.custom?.rollbarFingerprint === "string") {
    data.fingerprint = data.custom.rollbarFingerprint;
  }
}

function checkWebComponentScript() {
  var scripts = document.getElementsByTagName("script");
  var scriptFound = false;
  for (var i = 0; i < scripts.length; i++) {
    var src = scripts[i].getAttribute("src");
    if (src && src.includes("timelineembedded.js")) {
      scriptFound = true;
      break;
    }
  }
  return scriptFound;
}

const isWebComponent = checkWebComponentScript();

export const rollbar = new Rollbar({
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
  captureUncaught: true,
  captureUnhandledRejections: true,
  // Off: rrweb patches CSSOM/DOM app-wide and can't serialize Stripe's cross-origin iframes.
  // Empty triggers stop error items from requesting a replay that was never recorded.
  recorder: {
    enabled: false,
    triggers: [],
  },
  environment: isStaging ? "staging" : "production",
  // Rollbar is disabled in web component because it's capturing errors from the parent app
  enabled:
    (process.env.NODE_ENV === "production" || isStaging) && !isWebComponent,
  checkIgnore: function (isUncaught, args, payload) {
    // Check if this is a bot request
    const userAgent =
      typeof navigator !== "undefined" ? navigator.userAgent : "";
    const isBot = /bot|crawler|spider|crawling|PetalBot/i.test(userAgent);

    if (isBot) {
      return true; // discard bot errors
    }

    const message = payload.body?.trace?.exception?.message || "";
    const description = payload.body?.trace?.exception?.description || "";
    const filters = [
      "Object Not Found Matching Id",
      "Script error",
      "Connection to Indexed Database server lost. Refresh the page to try again",
    ];

    // Mirrors the rollbar.error() call in UseAuth.js — update both if the message changes.
    const isUseAuthRequestAbort =
      message === "Request aborted" &&
      description === "[UseAuth] GET_AUTH unexpected failure";

    if (
      isUseAuthRequestAbort ||
      filters.some(
        (filter) => message.includes(filter) || description.includes(filter),
      )
    ) {
      return true; // discard error
    }

    return false;
  },
  transform: promoteCustomFingerprint,
});

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error(error);
    // Send error to parent window if app is embedded in iframe
    if (window.self !== window.top) {
      window.parent.postMessage(
        {
          type: "error",
          content: error.message,
          metadata: {
            source: "embedded-timeline-iframe",
            componentStack: errorInfo.componentStack,
          },
        },
        "*",
      );
    }
    const userSignedIn = Boolean(auth.currentUser);
    rollbar.error(error, errorInfo, { userSignedIn });
    // An empty fallback hides the failed section; no error page was shown.
    if (isWebComponent || !this.props.fallback) return;
    customEvent({
      name: "error_boundary_shown",
      category: "Error",
      label: "Error boundary fallback rendered",
      data: {
        error_message: String(error?.message ?? "").slice(0, 100),
        user_signed_in: userSignedIn,
        user_id: (() => { try { return window.localStorage.getItem("stanza_uid") || undefined; } catch { return undefined; } })(),
        user_email: auth.currentUser?.email ?? undefined,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}
